import { z } from 'zod';

export const formFieldSchema = z.object({
  name: z.enum(['name', 'email', 'company', 'message']),
  label: z.string().min(1).max(80),
  type: z.enum(['text', 'email', 'textarea']),
  required: z.boolean(),
  maxLength: z.number().int().min(1).max(5000),
  autocomplete: z.string().optional(),
});
export const formDefinitionSchema = z.object({
  fields: z.array(formFieldSchema).min(1),
  submitLabel: z.string().min(1).max(40),
  successMessage: z.string().min(1).max(200),
  consentNotice: z.string().min(1).max(300),
});
export type FormDefinition = z.infer<typeof formDefinitionSchema>;

/** The minimum inquiry definition shipped for S1. Immutable once stored as a FormVersion. */
export const defaultInquiryForm: FormDefinition = {
  fields: [
    { name: 'name', label: 'Your name', type: 'text', required: true, maxLength: 120, autocomplete: 'name' },
    { name: 'email', label: 'Work email', type: 'email', required: true, maxLength: 254, autocomplete: 'email' },
    { name: 'company', label: 'Company', type: 'text', required: false, maxLength: 160, autocomplete: 'organization' },
    { name: 'message', label: 'What are you trying to build?', type: 'textarea', required: true, maxLength: 3000 },
  ],
  submitLabel: 'Send inquiry',
  successMessage: 'Thank you. We have your inquiry and will reply within one business day.',
  consentNotice: 'We use your details only to answer this inquiry.',
};

/** Builds the strict server-side payload validator from a stored form definition. */
export function buildSubmissionSchema(def: FormDefinition) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const f of def.fields) {
    let s: z.ZodString = z.string().trim().max(f.maxLength);
    if (f.type === 'email') s = s.email('Enter a valid email address');
    const base: z.ZodTypeAny = f.required ? s.min(1, `${f.label} is required`) : z.union([s, z.literal('')]).optional();
    shape[f.name] = base;
  }
  return z.object(shape).strict();
}

export const attributionSchema = z
  .object({
    utmSource: z.string().max(200).optional(),
    utmMedium: z.string().max(200).optional(),
    utmCampaign: z.string().max(200).optional(),
    utmTerm: z.string().max(200).optional(),
    utmContent: z.string().max(200).optional(),
    gclid: z.string().max(200).optional(),
    gbraid: z.string().max(200).optional(),
    wbraid: z.string().max(200).optional(),
    referrer: z.string().max(500).optional(),
    entryPath: z.string().max(500).optional(),
  })
  .strict();
export type AttributionInput = z.infer<typeof attributionSchema>;

export const consentSchema = z.object({
  analytics: z.boolean().default(false),
  advertising: z.boolean().default(false),
});
export type ConsentInput = z.infer<typeof consentSchema>;

export const submissionRequestSchema = z.object({
  contextToken: z.string().min(10).max(2000),
  idempotencyKey: z.string().min(16).max(100),
  values: z.record(z.string(), z.string()),
  attribution: attributionSchema.optional(),
  consent: consentSchema.default({ analytics: false, advertising: false }),
  /** Honeypot: real visitors leave it empty. */
  website: z.string().max(200).optional(),
});
export type SubmissionRequest = z.infer<typeof submissionRequestSchema>;

export const submissionResultSchema = z.object({
  submissionId: z.string(),
  duplicate: z.boolean(),
  message: z.string(),
});
export type SubmissionResult = z.infer<typeof submissionResultSchema>;
